import rest from '@modules/rest'
import upload from '@modules/upload'
import { getEmployeeInfoSchema } from '@services/systemField'
import { mapCustomFieldValues } from '@convenia/common-organisms/SystemFields/helpers'
import { PERSONAL_DOCUMENTS_AREA } from '@content/consts/Documents'
import {
  documentMappers as mappers,
  mapCustomFields,
  mapCustomDocument,
  mapCPFInput,
  mapRGInput,
  mapDriverLicenseInput,
  mapReservistInput,
  mapElectoralCardInput,
  mapCTPSInput
} from '@convenia/documents-organisms/content/mappers'
import { getFileUrl, getUploadEndpoint } from '@modules/fileHelpers'

export const getCustomDocuments = async (params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/custom-field-values/documents/employee`

    const { data } = await rest.get(url)

    const customDocuments = (data || [])
      .map(document => mapCustomDocument(document, { employeeId, getFileUrl }))

    return [ null, customDocuments ]
  } catch (err) {
    return [ err, null ]
  }
}

export const getCustomFields = async (getters) => {
  const employeeId = getters.getEmployeeUuid
  const companyId = getters.getCompanyUuid

  const [ err, data ] = await getEmployeeInfoSchema({
    employeeId,
    companyId,
    area: PERSONAL_DOCUMENTS_AREA.LABEL
  })

  const customFields = mapCustomFields(data)

  return [ err, customFields ]
}

export const getGeneralRegisters = async (params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/general-registers/employee`

    const { data } = await rest.get(url)

    const document = mappers.rg(data, { employeeId, getFileUrl })

    return [ null, document ]
  } catch (err) {
    return [ err, null ]
  }
}

export const updateGeneralRegisters = async (data, params) => {
  try {
    const { employeeId, companyId, schema } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/general-registers/employee`
    const body = mapRGInput({ data, schema })

    const response = await rest.post(url, { body })

    const document = mappers.rg(response?.data || {}, { employeeId, getFileUrl })

    return [ null, document ]
  } catch (err) {
    return [ err ]
  }
}

export const updateDriverLicense = async (data, params) => {
  try {
    const { employeeId, companyId, schema } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/driver-license/employee`
    const body = mapDriverLicenseInput({ data, schema })

    const response = await rest.post(url, { body })

    const document = mappers.driver_license(response?.data || {}, { employeeId, getFileUrl })

    return [ null, document ]
  } catch (err) {
    return [ err ]
  }
}

export const getDriverLicense = async (params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/driver-license/employee`

    const { data } = await rest.get(url)

    const document = mappers.driver_license(data, { employeeId, getFileUrl })

    return [ null, document ]
  } catch (err) {
    return [ err, null ]
  }
}

export const getCpf = async (params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/cpf/employee`

    const { data } = await rest.get(url)

    const document = mappers.cpf(data, { employeeId, getFileUrl })

    return [ null, document ]
  } catch (err) {
    return [ err, null ]
  }
}

export const updateCpf = async (data, params) => {
  try {
    const { employeeId, companyId, schema } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/cpf/employee`
    const body = mapCPFInput({ data, schema })

    const response = await rest.post(url, { body })

    const document = mappers.cpf(response?.data || {}, { employeeId, getFileUrl })

    return [ null, document ]
  } catch (err) {
    return [ err ]
  }
}

export const updateReservist = async (data, params) => {
  try {
    const { employeeId, companyId, schema } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/reservists/employee`
    const body = mapReservistInput({ data, schema })

    const response = await rest.post(url, { body })

    const document = mappers.reservist(response?.data || {}, { employeeId, getFileUrl })

    return [ null, document ]
  } catch (err) {
    return [ err ]
  }
}

export const getReservist = async (params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/reservists/employee`

    const { data } = await rest.get(url)

    const document = mappers.reservist(data, { employeeId, getFileUrl })

    return [ null, document ]
  } catch (err) {
    return [ err, null ]
  }
}

export const getElectoralCard = async (params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/electoral-card/employee`

    const { data } = await rest.get(url)

    const document = mappers.electoral_card(data, { employeeId, getFileUrl })

    return [ null, document ]
  } catch (err) {
    return [ err, null ]
  }
}

export const updateElectoralCard = async (data, params) => {
  try {
    const { employeeId, companyId, schema } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/electoral-card/employee`
    const body = mapElectoralCardInput({ data, schema })

    const response = await rest.post(url, { body })

    const document = mappers.electoral_card(response?.data || {}, { employeeId, getFileUrl })

    return [ null, document ]
  } catch (err) {
    return [ err ]
  }
}

export const getCtps = async (params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/ctps/employee`

    const { data } = await rest.get(url)

    const document = mappers.ctps(data, { employeeId, getFileUrl })

    return [ null, document ]
  } catch (err) {
    return [ err, null ]
  }
}

export const updateCtps = async (data, params) => {
  try {
    const { employeeId, companyId, schema } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/ctps/employee`
    const body = mapCTPSInput({ data, schema })

    const response = await rest.post(url, { body })

    const document = mappers.ctps(response?.data || {}, { employeeId, getFileUrl })

    return [ null, document ]
  } catch (err) {
    return [ err ]
  }
}

export const updateCustomDocument = async (data, params) => {
  try {
    const sectionId = data.id
    const { employeeId, companyId, schema } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/sections/${sectionId}/custom-field-values/employee`
    const body = {
      custom_fields: mapCustomFieldValues({
        data,
        schema,
        sectionId,
      })
    }

    await rest.put(url, { body })

    return [ null ]
  } catch (err) {
    return [ err ]
  }
}

export const uploadDocumentFiles = async ({ id, files, employeeId, type }) => {
  try {
    const endpoint = getUploadEndpoint(employeeId, id, type)

    await upload({ files, endpoint })

    return [ null, true ]
  } catch (err) {
    return [ err, null ]
  }
}
